import {
  ProductAdvancedSettingsBuilder,
  ProductSharedSettingsBuilder,
  ProductScheduleSettingsBuilder,
  ProductInventorySettingsBuilder
} from "./builders";
import {
  BasicQuestion,
  SelectManyQuestion,
  SelectOneQuestion,
  ShortAnswerQuestion
} from "../product/models/question";
import { ProductType, ScheduleType, QuestionType } from "./enums";
import { Product } from "./product.model";
import { ProductBundleSettingsBuilder } from "./builders/product.bundle.builder";
import * as moment from "moment";
import { CalendarEvent } from "../event/event.model";

export class ProductBuilder {
  public id?: string;
  public venueId?: string;
  
  public shared: ProductSharedSettingsBuilder;
  public advanced: ProductAdvancedSettingsBuilder;
  public schedule: ProductScheduleSettingsBuilder;
  public inventory: ProductInventorySettingsBuilder;
  public bundle: ProductBundleSettingsBuilder;
  public events: CalendarEvent[];
  public eventMap: Map<string, CalendarEvent>;
  public isCustomEventMap: Map<string, boolean>;

  constructor() {
    this.events = [];
    this.eventMap = new Map();
    this.isCustomEventMap = new Map();
    this.shared = new ProductSharedSettingsBuilder();
    this.advanced = new ProductAdvancedSettingsBuilder();
    this.schedule = new ProductScheduleSettingsBuilder();
    this.inventory = new ProductInventorySettingsBuilder();
    this.bundle = new ProductBundleSettingsBuilder();
  }

  build(): Product {
    this.events = Array.from(this.eventMap.values());
    return new Product(this);
  }

  edit(product: Product): ProductBuilder {
    this.id = product.id;
    this.eventMap = new Map();
    this.isCustomEventMap = new Map();
    this.events = product.events;
    this.events.map(event => {
      var key = moment(event.date).format("MM-DD-YY");
      this.eventMap.set(key, event);
      if (product.shared.name == "Custom Date") {
        console.log(event);
      }
      if (event.isCustomEvent) {
        this.isCustomEventMap.set(key, true);
      }
    });
    this.shared.edit(product.shared);
    this.schedule.edit(product.schedule);
    this.inventory.edit(product.inventory);
    this.advanced.edit(product.advanced);
    this.bundle.edit(product.bundle);
    return this;
  }

  loadAPIResponse(data: any): ProductBuilder {
    this.id = data.id;
    this.venueId = data.venueId;
    this.shared.load(data.shared);
    this.schedule.load(data.schedule);
    this.inventory.load(data.inventory);
    this.advanced.load(data.advanced);
    this.bundle.load(data.bundle);
    return this;
  }

  setVenue(venueId: string): ProductBuilder {
    this.venueId = venueId;
    return this;
  }

  get isActive(): boolean {
    return this.shared.isActive;
  }

  get name(): string {
    return this.shared.name;
  }

  get description(): string {
    return this.shared.description;
  }

  get instructions(): string {
    return this.shared.instructions;
  }

  get productType(): ProductType {
    return this.shared.type;
  }

  get serviceFee(): number {
    return this.shared.serviceFee;
  }

  get tips(): number[] {
    return this.shared.tips;
  }

  get warning(): string {
    return this.shared.warning;
  }

  get questions() {
    return this.shared.questions;
  }

  get productTypeMask(): string {
    return ProductType[this.productType];
  }

  get scheduleTypeMask(): string {
    return ScheduleType[this.scheduleType];
  }

  resetScheduleSettings() {
    this.schedule = new ProductScheduleSettingsBuilder();
  }
  resetInventorySettings() {
    this.inventory = new ProductInventorySettingsBuilder();
  }
  resetAdvancedSettings() {
    this.advanced = new ProductAdvancedSettingsBuilder();
  }

  setName(name: string): ProductBuilder {
    this.shared.name = name;
    return this;
  }

  setDescription(description: string): ProductBuilder {
    this.shared.description = description;
    return this;
  }

  setVisibility(isActive: boolean): ProductBuilder {
    this.shared.isActive = isActive;
    return this;
  }

  setInstructions(instructions: string): ProductBuilder {
    this.shared.instructions = instructions;
    return this;
  }

  setProductType(type: ProductType): ProductBuilder {
    this.shared.type = type;
    return this;
  }

  setWarning(warning: string): ProductBuilder {
    this.shared.warning = warning;
    return this;
  }
  setPrice(price: number) {
    this.inventory.price = price;
    return this;
  }

  addServiceFee(serviceFee: number): ProductBuilder {
    this.shared.serviceFee = serviceFee;
    return this;
  }

  removeServiceFee(): ProductBuilder {
    this.shared.serviceFee = null;
    this.shared.isFeePercentage = false;
    return this;
  }

  addTips(t1: number, t2: number, t3: number): ProductBuilder {
    this.shared.tips = [t1, t2, t3];
    return this;
  }
  removeTips(): ProductBuilder {
    this.shared.tips = null;
    return this;
  }

  initQuestions(): ProductBuilder {
    this.shared.questions = [];
    return this;
  }
  setQuestions(questions: BasicQuestion[]): ProductBuilder {
    this.shared.questions = questions;
    return this;
  }

  addQuestion(question: BasicQuestion): ProductBuilder {
    this.shared.questions.push(question);
    return this;
  }
  setFeePercentage(b: boolean): ProductBuilder {
    this.shared.isFeePercentage = b;
    return this;
  }

  setScheduleType(schedule: ScheduleType): ProductBuilder {
    this.schedule.type = schedule;
    return this;
  }
  get scheduleType(): ScheduleType {
    return this.schedule.type;
  }

  commit(form: any, questions: any[]) {
    this.setVisibility(form.isActive)
      .setName(form.passName)
      .setProductType(form.passType)
      .setDescription(form.passDescription)
      .setInstructions(form.passInstructions)
      .setWarning(form.warning);

    this.shared.redirect = form.redirect;
    this.shared.redirectUrl = form.redirectUrl;
    this.shared.isPrivate = form.isPrivate;
    if (form.passType == ProductType.Bundle) this.setPrice(form.passPrice);

    if (form.serviceFee) {
      this.addServiceFee(form.serviceFeeOpt).setFeePercentage(
        form.isFeePercentage
      );
    } else this.removeServiceFee();

    if (form.tippingOpts)
      this.addTips(form.tipOpt1, form.tipOpt2, form.tipOpt3);
    else this.removeTips();

    if (questions != []) this.shared.questions = [];
    for (let i = 0; i < questions.length; i++) {
      let f = questions[i].value;
      switch (f.type as QuestionType) {
        case QuestionType.SelectMany:
          this.addQuestion(
            new SelectManyQuestion(f.question, f.option, f.required)
          );
          break;
        case QuestionType.SelectOne:
          this.addQuestion(
            new SelectOneQuestion(f.question, f.options, f.required)
          );
          break;
        default:
          this.addQuestion(new ShortAnswerQuestion(f.question, f.required));
      }
    }
    return this;
  }

  create = (form: any, questions: any[]): Product =>
    this.commit(form, questions).build();
}
